/**
 * Contract Version Management Service
 *
 * Handles multi-version contract system with soft void pattern.
 * Max 5 versions per recruit enforced at database level.
 *
 * Key Concepts:
 * - Only ONE active contract per recruit (enforced by unique index)
 * - Voiding creates new version, deactivates old
 * - Parent-child lineage tracked via parent_instance_id
 * - Full audit trail preserved (voided_at, voided_by, void_reason)
 */

import { supabase } from '@/utils/supabase';
import type { ContractInstanceData } from '@/types/recruitment';
import { contractInstanceFromDatabase } from '@/types/recruitment';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ContractVersionService', 'recruitment');

// ============================================================================
// Types
// ============================================================================

export interface VoidContractResult {
  success: boolean;
  newInstanceId?: string;
  error?: string;
  versionNumber?: number;
}

export interface VoidValidation {
  allowed: boolean;
  reason?: string;
}

export interface VersionHistoryItem extends ContractInstanceData {
  voidedByUser?: {
    full_name: string;
    email: string;
  };
}

// ============================================================================
// Core Version Management Functions
// ============================================================================

/**
 * Get the currently active contract instance for a recruit
 * This is the "working" version shown in the main UI
 *
 * @param recruitId - Recruit ID
 * @returns Active contract instance or null if none exists
 */
export async function getActiveContractInstance(
  recruitId: string
): Promise<ContractInstanceData | null> {
  const timer = log.startTimer('get_active_contract');

  try {
    const { data, error } = await supabase
      .from('contract_instances')
      .select('*')
      .eq('recruit_id', recruitId)
      .eq('is_active', true)
      .single();

    if (error) {
      // Not finding an active instance is not an error (might be all voided)
      if (error.code === 'PGRST116') {
        log.info('No active contract instance found', { recruitId });
        log.endTimer(timer, { found: false });
        return null;
      }
      throw error;
    }

    const instance = contractInstanceFromDatabase(data);
    log.endTimer(timer, { found: true, instanceId: instance.id });
    return instance;

  } catch (error) {
    log.error('Failed to get active contract instance', { recruitId }, error as Error);
    log.endTimer(timer, { success: false });
    return null;
  }
}

/**
 * Get complete version history for a recruit
 * Returns all versions (active + voided) in reverse chronological order
 *
 * @param recruitId - Recruit ID
 * @returns Array of all contract versions with void metadata
 */
export async function getContractVersionHistory(
  recruitId: string
): Promise<VersionHistoryItem[]> {
  const timer = log.startTimer('get_version_history');

  try {
    const { data, error } = await supabase
      .from('contract_instances')
      .select(`
        *,
        voided_by_user:voided_by(
          id,
          email,
          full_name,
          first_name,
          last_name
        )
      `)
      .eq('recruit_id', recruitId)
      .order('created_at', { ascending: false });

    if (error) {
throw error;
}

    if (!data || data.length === 0) {
      log.info('No contract history found', { recruitId });
      log.endTimer(timer, { count: 0 });
      return [];
    }

    const history = data.map(row => {
      const instance = contractInstanceFromDatabase(row);

      // Add voided by user information
      if (row.voided_by_user) {
        const userData = row.voided_by_user;
        return {
          ...instance,
          voidedByUser: {
            full_name: userData.full_name ||
                       `${userData.first_name || ''} ${userData.last_name || ''}`.trim() ||
                       'Unknown User',
            email: userData.email
          }
        };
      }

      return instance;
    });

    log.endTimer(timer, { count: history.length });
    return history;

  } catch (error) {
    log.error('Failed to get contract version history', { recruitId }, error as Error);
    log.endTimer(timer, { success: false });
    return [];
  }
}

/**
 * Validate if a contract can be voided
 *
 * Business Rules:
 * - Can't void if both parties have signed (fully executed)
 * - Can't void if already voided
 * - Must have admin or recruiter role
 *
 * @param instance - Contract instance to validate
 * @param userRole - Current user's role
 * @returns Validation result with reason if not allowed
 */
export function canVoidContract(
  instance: ContractInstanceData,
  userRole: string
): VoidValidation {
  // Rule 1: Can't void fully executed contracts
  if (instance.hasLoSignature && instance.hasRecruiterSignature) {
    return {
      allowed: false,
      reason: 'Fully executed contracts cannot be voided. Contact legal for amendments.'
    };
  }

  // Rule 2: Can't void if already voided
  if (instance.status === 'voided') {
    return {
      allowed: false,
      reason: 'Contract is already voided'
    };
  }

  // Rule 3: Permission check
  if (!['admin', 'recruiter'].includes(userRole)) {
    return {
      allowed: false,
      reason: 'Insufficient permissions to void contracts'
    };
  }

  return { allowed: true };
}

/**
 * Validate void reason meets requirements
 *
 * @param reason - Void reason text
 * @returns Validation result
 */
export function validateVoidReason(reason: string): VoidValidation {
  const MIN_LENGTH = 20;
  const MAX_LENGTH = 500;

  if (!reason || reason.trim().length === 0) {
    return {
      allowed: false,
      reason: 'Void reason is required'
    };
  }

  const trimmed = reason.trim();

  if (trimmed.length < MIN_LENGTH) {
    return {
      allowed: false,
      reason: `Please provide detailed reason (minimum ${MIN_LENGTH} characters)`
    };
  }

  if (trimmed.length > MAX_LENGTH) {
    return {
      allowed: false,
      reason: `Reason too long (maximum ${MAX_LENGTH} characters)`
    };
  }

  return { allowed: true };
}

/**
 * Void current contract version and create new draft
 *
 * Atomic transaction:
 * 1. Validate void is allowed
 * 2. Get current instance details
 * 3. Calculate next version number
 * 4. Deactivate current instance (status='voided', is_active=false)
 * 5. Create new version (inherits content, clears signatures)
 * 6. Reset recruit status to 'Inquiry'
 * 7. Database trigger auto-deletes oldest voided if at 5-version cap
 *
 * @param instanceId - Contract instance ID to void
 * @param userId - User performing void action
 * @param voidReason - Required reason for voiding (20-500 chars)
 * @returns Result with new instance ID or error
 */
export async function voidAndCreateNewVersion(
  instanceId: string,
  userId: string,
  voidReason: string
): Promise<VoidContractResult> {
  const timer = log.startTimer('void_and_create_version');

  try {
    // Step 1: Validate void reason
    const reasonValidation = validateVoidReason(voidReason);
    if (!reasonValidation.allowed) {
      return {
        success: false,
        error: reasonValidation.reason
      };
    }

    // Step 2: Get current instance
    const { data: currentInstance, error: fetchError } = await supabase
      .from('contract_instances')
      .select('*')
      .eq('id', instanceId)
      .single();

    if (fetchError || !currentInstance) {
      log.error('Contract instance not found', { instanceId }, fetchError as Error);
      return {
        success: false,
        error: 'Contract instance not found'
      };
    }

    // Convert to typed object for validation
    const instance = contractInstanceFromDatabase(currentInstance);

    // Step 3: Validate can void (business rules)
    // Note: We don't pass userRole here since we validate on frontend
    // Backend check would require fetching user role from auth
    if (instance.hasLoSignature && instance.hasRecruiterSignature) {
      return {
        success: false,
        error: 'Cannot void fully executed contracts'
      };
    }

    if (instance.status === 'voided') {
      return {
        success: false,
        error: 'Contract is already voided'
      };
    }

    // Step 4: Calculate next version number
    const { data: allVersions, error: versionError } = await supabase
      .from('contract_instances')
      .select('version_number')
      .eq('recruit_id', currentInstance.recruit_id)
      .order('version_number', { ascending: false })
      .limit(1);

    if (versionError) {
      throw versionError;
    }

    const nextVersion = (allVersions?.[0]?.version_number ?? 0) + 1;

    // Step 5: Void current instance (deactivate)
    const { error: voidError } = await supabase
      .from('contract_instances')
      .update({
        status: 'voided',
        is_active: false,
        voided_at: new Date().toISOString(),
        voided_by: userId,
        void_reason: voidReason.trim()
      })
      .eq('id', instanceId);

    if (voidError) {
      log.error('Failed to void contract instance', { instanceId }, voidError);
      return {
        success: false,
        error: 'Failed to void contract'
      };
    }

    log.info('Contract instance voided', {
      instanceId,
      versionNumber: currentInstance.version_number,
      reason: voidReason.substring(0, 50)
    });

    // Step 6: Create new version (inherits content, fresh signatures)
    const { data: newInstance, error: createError } = await supabase
      .from('contract_instances')
      .insert({
        organization_id: currentInstance.organization_id,
        recruit_id: currentInstance.recruit_id,
        template_id: currentInstance.template_id,
        template_version: currentInstance.template_version,

        // Copy contract content
        sections: currentInstance.sections,
        variable_data: currentInstance.variable_data,
        checkbox_states: currentInstance.checkbox_states,

        // Version metadata
        version_number: nextVersion,
        parent_instance_id: instanceId,
        is_active: true,

        // Reset workflow
        status: 'draft',
        sent_at: null,
        viewed_at: null,
        accepted_at: null,

        // Clear ALL signature fields
        signature_date: null,
        signature_data_url: null,
        has_lo_signature: false,

        loan_officer_signature_date: null,
        loan_officer_signature_data_url: null,
        loan_officer_signature_method: null,
        loan_officer_signature_ip: null,
        loan_officer_signature_user_agent: null,
        loan_officer_signature_audit_log: null,
        loan_officer_consent_electronic: false,
        loan_officer_intent_confirmed: false,
        loan_officer_terms_accepted: false,
        loan_officer_accuracy_certified: false,

        recruiter_signature_date: null,
        recruiter_signature_data_url: null,
        recruiter_signature_method: null,
        recruiter_signature_ip_address: null,
        recruiter_signature_user_agent: null,
        recruiter_signature_audit_log: null,
        recruiter_consent_electronic: false,
        recruiter_intent_confirmed: false,
        recruiter_terms_accepted: false,
        recruiter_accuracy_certified: false,
        has_recruiter_signature: false,

        final_pdf_url: null,

        // Audit
        created_by: userId
      })
      .select()
      .single();

    if (createError) {
      log.error('Failed to create new version', {
        instanceId,
        recruitId: currentInstance.recruit_id
      }, createError);

      // Rollback: Reactivate voided instance
      await supabase
        .from('contract_instances')
        .update({
          is_active: true,
          status: currentInstance.status,
          voided_at: null,
          voided_by: null,
          void_reason: null
        })
        .eq('id', instanceId);

      return {
        success: false,
        error: 'Failed to create new version'
      };
    }

    log.info('New contract version created', {
      newInstanceId: newInstance.id,
      versionNumber: nextVersion,
      parentInstanceId: instanceId
    });

    // Step 7: Reset recruit status and clear signing-related fields
    const { error: statusError } = await supabase
      .from('recruits')
      .update({
        status: 'Inquiry',
        // Clear commitment fields to require re-signing
        commitment_signed_at: null,
        commitment_data: null,
        commitment_signature_name: null,
        // Reset signing steps (keep step 6 - ID uploads remain valid)
        step_5_completed: false,  // Compensation Agreement
        step_7_completed: false,  // Commitment
        updated_by: userId,
        updated_at: new Date().toISOString()
      })
      .eq('id', currentInstance.recruit_id);

    if (statusError) {
      log.warn('Failed to update recruit status after void', {
        recruitId: currentInstance.recruit_id
      }, statusError);
      // Don't fail the whole operation for status update failure
    }

    log.endTimer(timer, {
      success: true,
      newVersion: nextVersion,
      newInstanceId: newInstance.id
    });

    return {
      success: true,
      newInstanceId: newInstance.id,
      versionNumber: nextVersion
    };

  } catch (error) {
    log.error('Failed to void and create new version', { instanceId }, error as Error);
    log.endTimer(timer, { success: false });

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Get version count for a recruit
 * Useful for showing "X of 5 versions used" warning
 *
 * @param recruitId - Recruit ID
 * @returns Object with total, active, and voided counts
 */
export async function getVersionCount(recruitId: string): Promise<{
  total: number;
  active: number;
  voided: number;
}> {
  try {
    const { data, error } = await supabase
      .from('contract_instances')
      .select('id, is_active, status')
      .eq('recruit_id', recruitId);

    if (error) {
throw error;
}

    const total = data?.length ?? 0;
    const active = data?.filter(d => d.is_active).length ?? 0;
    const voided = data?.filter(d => d.status === 'voided').length ?? 0;

    return { total, active, voided };

  } catch (error) {
    log.error('Failed to get version count', { recruitId }, error as Error);
    return { total: 0, active: 0, voided: 0 };
  }
}
